let refreshPromise = null;

export const useApi = (accessToken) => {
  const makeRequest = async (endpoint, options = {}) => {
    const { skipAuthRefresh = false, skipAuthHeader = false, ...fetchOptions } = options;

    // Add v2 prefix for v2 endpoints
    const url = endpoint.startsWith('/v2')
      ? `/api${endpoint}`
      : endpoint;

    const token = accessToken || useTokens().access;
    const isRefreshEndpoint = url.includes('/api/login/refresh/');
    const shouldRefreshOn401 = !skipAuthRefresh && !isRefreshEndpoint;
    const shouldIncludeAuthHeader = token && !skipAuthHeader && !isRefreshEndpoint;

    try {
      const response = await $fetch(url, {
        ...fetchOptions,
        headers: {
          'Content-Type': 'application/json',
          ...(shouldIncludeAuthHeader ? { 'Authorization': `Bearer ${token}` } : {}),
          ...fetchOptions.headers
        }
      });

      return response;
    } catch(error) {
      if (error?.data?.code === 'token_not_valid' && shouldRefreshOn401) {
        if (!refreshPromise) {
          refreshPromise = useRefreshAccessToken(useTokens().refresh)
            .then((newAccessToken) => {
              if (!newAccessToken) throw new Error('Failed to refresh access token');
              useTokens().setAccessToken(newAccessToken);
              useUser().setTokens(newAccessToken, useTokens().refresh);
              return newAccessToken;
            })
            .catch(async (refreshError) => {
              await useLogout('/login');
              throw refreshError;
            })
            .finally(() => {
              refreshPromise = null;
            });
        }

        let newAccessToken;
        try {
          newAccessToken = await refreshPromise;
        } catch (refreshError) {
          return { error: refreshError };
        }

        // Retry the original request with the new access token
        const retryResponse = await $fetch(url, {
          ...fetchOptions,
          headers: {
            'Content-Type': 'application/json',
            ...fetchOptions.headers,
            'Authorization': `Bearer ${newAccessToken}`
          }
        });
        return retryResponse;
      }

      return {
        error
      }
    }
  }

  return {
    get: (endpoint, requestOptions = {}) => makeRequest(endpoint, requestOptions),
    post: (endpoint, data, requestOptions = {}) =>
      makeRequest(endpoint, {
        ...requestOptions,
        method: 'POST',
        body: data
      }),
    put: (endpoint, data, requestOptions = {}) =>
      makeRequest(endpoint, {
        ...requestOptions,
        method: 'PUT',
        body: data
      }),
    delete: (endpoint, data, requestOptions = {}) =>
      makeRequest(endpoint, {
        ...requestOptions,
        method: 'DELETE',
        body: data
      })
  }
}
